import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  TextField,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close as CloseIcon,
  NavigateBefore,
  NavigateNext,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  BugReport as BugReportIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import FeedbackDialog from "../FeedbackDialog/FeedbackDialog";
import { SearchResult } from "../../types/chat.types";

import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerProps {
  open: boolean;
  onClose: () => void;
  reference: SearchResult;
  isDirectLink?: boolean;
}

const API_BASE_URL = "/api2";

// How many pages around the reference to render initially
const INITIAL_BUFFER = 2;
// How many pages to add when scrolling near an edge
const PAGES_PER_LOAD = 3;
// Distance from scroll edge (px) that triggers loading more pages
const SCROLL_THRESHOLD = 400;

interface PageTextInfo {
  fullText: string;
  spanMap: { span: HTMLSpanElement; start: number; end: number }[];
}

const PdfViewer: React.FC<PdfViewerProps> = ({
  open,
  onClose,
  reference,
  isDirectLink = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.5);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [documentTitle, setDocumentTitle] = useState<string>("");
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [pageInputValue, setPageInputValue] = useState<string>("1");

  // Expanding render window: only pages in [start, end] are in the DOM
  const [renderRange, setRenderRange] = useState({ start: 1, end: 1 });

  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const scrollFixRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  const isScrollingToPage = useRef(false);
  const hasScrolledToRef = useRef(false);
  const highlightedPages = useRef<Set<number>>(new Set());
  const [actualHighlightPages, setActualHighlightPages] = useState<Set<number>>(new Set());
  const pageTextData = useRef<Map<number, PageTextInfo>>(new Map());
  const hasMatchedChunk = useRef(false);

  // Calculate PDF page width to fit container on mobile
  const getPageWidth = useCallback(() => {
    if (!isMobile) return undefined;
    // On fullscreen mobile, use window width directly to avoid
    // MUI padding/margin reducing the measured container width
    return window.innerWidth;
  }, [isMobile]);

  // Format document name (GA013.pdf -> GA013)
  const formatDocumentName = (docName: string): string => {
    if (!docName) return "";
    return docName.replace(/\.pdf$/i, "");
  };

  // Load PDF when dialog opens
  useEffect(() => {
    if (open && reference?.document_name) {
      setLoading(true);
      setError(null);

      const filename = reference.document_name.includes(".")
        ? reference.document_name
        : `${reference.document_name}.pdf`;

      const url = `${API_BASE_URL}/api/documents/${encodeURIComponent(filename)}/download`;
      setPdfUrl(url);
      setDocumentTitle(reference.title || formatDocumentName(filename));

      const initialPage = reference.page_number || 1;
      setCurrentPage(initialPage);
      setPageInputValue(String(initialPage));
      hasScrolledToRef.current = false;
      highlightedPages.current = new Set();
      setActualHighlightPages(new Set());
      pageTextData.current = new Map();
      hasMatchedChunk.current = false;
    } else if (!open) {
      setPdfUrl(null);
      setNumPages(0);
      setCurrentPage(1);
      setScale(1.5);
      setError(null);
      setDocumentTitle("");
      setPageInputValue("1");
      setRenderRange({ start: 1, end: 1 });
      pageRefs.current.clear();
      hasScrolledToRef.current = false;
      highlightedPages.current = new Set();
      setActualHighlightPages(new Set());
      pageTextData.current = new Map();
      hasMatchedChunk.current = false;
    }
  }, [open, reference]);

  // Initialize render range when PDF loads
  useEffect(() => {
    if (numPages > 0) {
      const refPage = reference?.page_number || 1;
      const start = Math.max(1, refPage - INITIAL_BUFFER);
      const end = Math.min(numPages, refPage + INITIAL_BUFFER);
      setRenderRange({ start, end });
    }
  }, [numPages, reference?.page_number]);

  // Fix scroll position after prepending pages (upward expansion)
  useLayoutEffect(() => {
    if (scrollFixRef.current && containerRef.current) {
      const { scrollTop, scrollHeight } = scrollFixRef.current;
      const newScrollHeight = containerRef.current.scrollHeight;
      containerRef.current.scrollTop = scrollTop + (newScrollHeight - scrollHeight);
      scrollFixRef.current = null;
    }
  });

  // Scroll handler: expand range on edges + track current page
  useEffect(() => {
    const container = containerRef.current;
    if (!container || numPages === 0) return;

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        ticking = false;
        if (isScrollingToPage.current) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const scrollBottom = scrollHeight - scrollTop - clientHeight;

        // Expand downward
        if (scrollBottom < SCROLL_THRESHOLD) {
          setRenderRange((prev) => {
            if (prev.end >= numPages) return prev;
            return { ...prev, end: Math.min(numPages, prev.end + PAGES_PER_LOAD) };
          });
        }

        // Expand upward
        if (scrollTop < SCROLL_THRESHOLD) {
          setRenderRange((prev) => {
            if (prev.start <= 1) return prev;
            // Save scroll metrics for position fix (only once per expansion)
            if (!scrollFixRef.current) {
              scrollFixRef.current = {
                scrollTop: container.scrollTop,
                scrollHeight: container.scrollHeight,
              };
            }
            return { ...prev, start: Math.max(1, prev.start - PAGES_PER_LOAD) };
          });
        }

        // Track current page based on which page is most centered in viewport
        const containerRect = container.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;
        let closestPage = currentPage;
        let closestDist = Infinity;

        pageRefs.current.forEach((el, pageNum) => {
          const rect = el.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          const dist = Math.abs(center - containerCenter);
          if (dist < closestDist) {
            closestDist = dist;
            closestPage = pageNum;
          }
        });

        if (closestPage !== currentPage) {
          setCurrentPage(closestPage);
          setPageInputValue(String(closestPage));
        }
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => container.removeEventListener("scroll", handleScroll);
  }, [numPages, currentPage]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (err: Error) => {
    console.error("PDF load error:", err);
    setError("PDF konnte nicht geladen werden. Bitte versuchen Sie es erneut.");
    setLoading(false);
  };

  // Match chunk text across all rendered pages' combined text using segment-based multi-match
  const tryHighlightAcrossPages = useCallback(() => {
    if (hasMatchedChunk.current) return;

    const rawMatchText = reference?.context || reference?.excerpt;
    if (!rawMatchText) return;

    // Clean chunk text: remove copyright lines and isolated page numbers
    const matchText = rawMatchText
      .replace(/Copyright\s+Rudolf\s+Steiner\s+Nachlass-Verwaltung\s+Buch:\s*\d+\s+Seite:\s*\d+/gi, " ")
      .replace(/\n\s*\d{1,4}\s*\n/g, "\n")
      .trim();

    // Sort all extracted pages by number
    const sortedPages = Array.from(pageTextData.current.entries())
      .sort(([a], [b]) => a - b);
    if (sortedPages.length === 0) return;

    // Build combined text with page boundary tracking
    let combinedText = "";
    const pageBoundaries: { pageNum: number; startPos: number; endPos: number }[] = [];

    for (const [pageNum, info] of sortedPages) {
      const startPos = combinedText.length;
      combinedText += info.fullText;
      pageBoundaries.push({ pageNum, startPos, endPos: combinedText.length });
    }

    // Normalize for segment matching: strip whitespace + lowercase + unify typographic chars
    const strip = (s: string) =>
      s.replace(/[\s\u00AD\u200B-\u200D\uFEFF]/g, "")
       .replace(/[""„«»]/g, '"')
       .replace(/[''‚‹›]/g, "'")
       .replace(/[–—―]/g, "-")
       .replace(/\u00e4/g, "ae").replace(/\u00f6/g, "oe").replace(/\u00fc/g, "ue")
       .replace(/\u00c4/g, "ae").replace(/\u00d6/g, "oe").replace(/\u00dc/g, "ue")
       .replace(/\u00df/g, "ss")
       .toLowerCase();

    // Build stripped version AND position map together to keep them in sync
    // (strip() expands umlauts like ä→ae, so posMap must account for that)
    let combinedStripped = "";
    const posMap: number[] = []; // posMap[strippedIdx] = original combinedText index

    const isWs = (ch: string) => /[\s\u00AD\u200B-\u200D\uFEFF]/.test(ch);

    for (let i = 0; i < combinedText.length; i++) {
      const ch = combinedText[i];
      if (isWs(ch)) continue;

      let mapped = ch.toLowerCase();
      if (/[""„«»]/.test(ch)) mapped = '"';
      else if (/[''‚‹›]/.test(ch)) mapped = "'";
      else if (/[–—―]/.test(ch)) mapped = "-";
      else if (ch === "\u00e4" || ch === "\u00c4") mapped = "ae";
      else if (ch === "\u00f6" || ch === "\u00d6") mapped = "oe";
      else if (ch === "\u00fc" || ch === "\u00dc") mapped = "ue";
      else if (ch === "\u00df") mapped = "ss";

      for (let j = 0; j < mapped.length; j++) {
        combinedStripped += mapped[j];
        posMap.push(i);
      }
    }

    const chunkStripped = strip(matchText);

    if (chunkStripped.length < 10 || combinedStripped.length === 0) return;

    console.log("[PdfViewer] Chunk text (cleaned):", matchText.substring(0, 120) + "...");

    // Split chunk into segments at sentence boundaries, min ~40 chars per segment
    const sentences = matchText.split(/(?<=\.)\s+/);
    const segments: string[] = [];
    let buffer = "";
    for (const sentence of sentences) {
      buffer += (buffer ? " " : "") + sentence;
      if (buffer.length >= 40) {
        segments.push(buffer);
        buffer = "";
      }
    }
    if (buffer.length > 0) {
      if (segments.length > 0 && buffer.length < 20) {
        segments[segments.length - 1] += " " + buffer;
      } else {
        segments.push(buffer);
      }
    }

    // Find each segment in the combined stripped text
    const matchedRanges: { start: number; end: number }[] = [];

    for (const segment of segments) {
      const segStripped = strip(segment);
      if (segStripped.length < 15) continue;

      // Try full segment, then progressively shorter prefixes
      const tryLens = [
        segStripped.length,
        Math.min(80, segStripped.length),
        Math.min(40, segStripped.length),
      ];

      for (const len of tryLens) {
        if (len < 15) continue;
        const search = segStripped.substring(0, len);
        const idx = combinedStripped.indexOf(search);
        if (idx !== -1) {
          // Extend match as far as possible
          let matchLen = len;
          for (let k = len; k < segStripped.length && idx + k < combinedStripped.length; k++) {
            if (combinedStripped[idx + k] === segStripped[k]) {
              matchLen = k + 1;
            } else {
              break;
            }
          }
          // Map back to original positions
          const origStart = posMap[idx] ?? 0;
          const origEnd = (posMap[Math.min(idx + matchLen - 1, posMap.length - 1)] ?? origStart) + 1;
          matchedRanges.push({ start: origStart, end: origEnd });
          break;
        }
      }
    }

    if (matchedRanges.length === 0) return;

    // Merge all ranges into one continuous span from first to last match
    matchedRanges.sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [{
      start: matchedRanges[0].start,
      end: matchedRanges[matchedRanges.length - 1].end,
    }];

    console.log("[PdfViewer] Highlight ranges:", merged.length, "merged from", matchedRanges.length, "segment matches (of", segments.length, "segments)");

    // Apply highlights for all merged ranges
    let anyHighlighted = false;

    for (const range of merged) {
      for (const { pageNum, startPos, endPos } of pageBoundaries) {
        if (range.end <= startPos || range.start >= endPos) continue;

        const localStart = Math.max(0, range.start - startPos);
        const localEnd = Math.min(endPos - startPos, range.end - startPos);

        const pageInfo = pageTextData.current.get(pageNum);
        if (!pageInfo) continue;

        // Mark page as highlighted (for blue border) without applying text-level highlighting
        if (localEnd > localStart) {
          highlightedPages.current.add(pageNum);
          anyHighlighted = true;
        }
      }
    }

    if (anyHighlighted) {
      hasMatchedChunk.current = true;
      setActualHighlightPages(new Set(highlightedPages.current));

      // Scroll to first highlighted span
      const firstRange = merged[0];
      for (const { pageNum, startPos } of pageBoundaries) {
        if (!highlightedPages.current.has(pageNum)) continue;
        const pageInfo = pageTextData.current.get(pageNum);
        if (!pageInfo) continue;
        const localStart = Math.max(0, firstRange.start - startPos);
        const firstSpan = pageInfo.spanMap.find(s => s.end > localStart);
        if (firstSpan) {
          hasScrolledToRef.current = true;
          firstSpan.span.scrollIntoView({ behavior: "auto", block: "center" });
          break;
        }
      }
    }
  }, [reference]);

  // Extract text from a rendered page's text layer and store for cross-page matching
  const extractPageText = useCallback(
    (pageElement: HTMLDivElement, pageNum: number, retriesLeft: number = 10) => {
      if (hasMatchedChunk.current) return;

      if (pageTextData.current.has(pageNum)) {
        tryHighlightAcrossPages();
        return;
      }

      const textLayer =
        pageElement.querySelector(".textLayer") ||
        pageElement.querySelector(".react-pdf__Page__textContent") ||
        pageElement.querySelector('[data-main-rotation]');

      if (!textLayer) {
        if (retriesLeft > 0) {
          setTimeout(() => extractPageText(pageElement, pageNum, retriesLeft - 1), 400);
        }
        return;
      }

      const spans = Array.from(textLayer.querySelectorAll("span")) as HTMLSpanElement[];
      if (spans.length === 0) {
        if (retriesLeft > 0) {
          setTimeout(() => extractPageText(pageElement, pageNum, retriesLeft - 1), 400);
        }
        return;
      }

      let fullText = "";
      const spanMap: { span: HTMLSpanElement; start: number; end: number }[] = [];

      for (const span of spans) {
        const text = span.textContent || "";
        if (!text) continue;
        const start = fullText.length;
        fullText += text;
        spanMap.push({ span, start, end: fullText.length });
      }

      if (fullText.length === 0) return;

      pageTextData.current.set(pageNum, { fullText, spanMap });
      tryHighlightAcrossPages();
    },
    [tryHighlightAcrossPages]
  );

  // After a page renders: scroll to reference page + extract text for highlighting
  const handlePageRenderSuccess = useCallback(
    (pageNum: number) => {
      const targetPage = reference?.page_number;

      const pageEl = pageRefs.current.get(pageNum);
      if (!pageEl) return;

      // Scroll to the target page when it renders
      if (targetPage !== undefined && pageNum === targetPage && !hasScrolledToRef.current) {
        hasScrolledToRef.current = true;
        setTimeout(() => {
          // Don't override if highlight scroll already happened
          if (hasMatchedChunk.current) return;
          isScrollingToPage.current = true;
          pageEl.scrollIntoView({ behavior: "auto", block: "start" });
          setTimeout(() => { isScrollingToPage.current = false; }, 300);
        }, 150);
      }

      // Extract text for cross-page highlighting
      const matchText = reference?.context || reference?.excerpt;
      if (matchText) {
        extractPageText(pageEl, pageNum, 10);
      }
    },
    [reference, extractPageText]
  );

  // Navigate to a page, re-centering the render window if needed
  const goToPage = useCallback(
    (page: number) => {
      if (page < 1 || page > numPages) return;

      setCurrentPage(page);
      setPageInputValue(String(page));

      // Page outside current render range → re-center window
      if (page < renderRange.start || page > renderRange.end) {
        setRenderRange({
          start: Math.max(1, page - INITIAL_BUFFER),
          end: Math.min(numPages, page + INITIAL_BUFFER),
        });
        setTimeout(() => {
          const el = pageRefs.current.get(page);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      } else {
        isScrollingToPage.current = true;
        const el = pageRefs.current.get(page);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setTimeout(() => {
          isScrollingToPage.current = false;
        }, 600);
      }
    },
    [numPages, renderRange]
  );

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageInputValue(e.target.value);
  };

  const handlePageInputSubmit = () => {
    const page = parseInt(pageInputValue, 10);
    if (!isNaN(page)) {
      goToPage(page);
    }
  };

  const handlePageInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handlePageInputSubmit();
    }
  };

  const zoomIn = () => setScale((s) => Math.min(s + 0.25, 3.0));
  const zoomOut = () => setScale((s) => Math.max(s - 0.25, 0.5));

  const handleOpenFeedback = () => setFeedbackDialogOpen(true);
  const handleCloseFeedback = () => setFeedbackDialogOpen(false);

  const handleOpenInBrowser = () => {
    if (pdfUrl) {
      window.open(pdfUrl, "_blank");
    }
  };

  // Array of page numbers to render
  const pageNumbers = useMemo(() => {
    if (renderRange.end < renderRange.start || numPages === 0) return [];
    return Array.from(
      { length: renderRange.end - renderRange.start + 1 },
      (_, i) => renderRange.start + i
    );
  }, [renderRange, numPages]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      fullScreen={isMobile}
      sx={!isMobile ? { "& .MuiDialog-paper": { height: "95vh" } } : undefined}
    >
      <DialogTitle sx={{ pb: 1, px: isMobile ? 1.5 : 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant={isMobile ? "subtitle1" : "h6"}
            component="div"
            sx={isMobile ? { fontSize: "0.95rem", lineHeight: 1.3 } : undefined}
            noWrap={isMobile}
          >
            {formatDocumentName(reference?.document_name || "")}
            {!isMobile &&
              documentTitle &&
              documentTitle !== formatDocumentName(reference?.document_name || "") &&
              ` - ${documentTitle}`}
          </Typography>
          <Box display="flex" alignItems="center" gap={isMobile ? 0.5 : 1}>
            {/* Open in Browser */}
            {isMobile ? (
              <IconButton
                size="small"
                onClick={handleOpenInBrowser}
                title="Im Browser öffnen"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                onClick={handleOpenInBrowser}
                sx={{ textTransform: "none" }}
              >
                Im Browser öffnen
              </Button>
            )}
            {/* Feedback Button */}
            {isMobile ? (
              <IconButton
                size="small"
                onClick={handleOpenFeedback}
                sx={{ color: "#ff9800" }}
                title="Problem melden"
              >
                <BugReportIcon fontSize="small" />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<BugReportIcon />}
                onClick={handleOpenFeedback}
                sx={{
                  textTransform: "none",
                  borderColor: "#ff9800",
                  color: "#ff9800",
                  "&:hover": {
                    borderColor: "#f57c00",
                    backgroundColor: "rgba(255, 152, 0, 0.04)",
                  },
                }}
              >
                Problem melden
              </Button>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Navigation Controls */}
        <Box
          sx={{
            mt: isMobile ? 1 : 2,
            display: "flex",
            alignItems: "center",
            gap: isMobile ? 0.5 : 1,
            flexWrap: "wrap",
          }}
        >
          <IconButton
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1}
            size="small"
            title="Vorherige Seite"
          >
            <NavigateBefore />
          </IconButton>

          <TextField
            size="small"
            value={pageInputValue}
            onChange={handlePageInputChange}
            onBlur={handlePageInputSubmit}
            onKeyPress={handlePageInputKeyPress}
            sx={{ width: isMobile ? 50 : 60 }}
            inputProps={{
              style: { textAlign: "center", padding: isMobile ? "4px" : undefined },
            }}
          />

          <Typography variant="body2" sx={{ mx: 0.5 }}>
            / {numPages}
          </Typography>

          <IconButton
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= numPages}
            size="small"
            title="Nächste Seite"
          >
            <NavigateNext />
          </IconButton>

          {/* Zoom Controls - hidden on mobile */}
          {!isMobile && (
            <Box sx={{ ml: 2, display: "flex", alignItems: "center", gap: 0.5 }}>
              <IconButton onClick={zoomOut} size="small" title="Verkleinern">
                <ZoomOutIcon />
              </IconButton>
              <Chip
                label={`${Math.round(scale * 100)}%`}
                size="small"
                variant="outlined"
              />
              <IconButton onClick={zoomIn} size="small" title="Vergrößern">
                <ZoomInIcon />
              </IconButton>
            </Box>
          )}

          {/* Reference Page Indicator */}
          {reference?.page_number && (
            <Box sx={{ ml: "auto" }}>
              <Chip
                label={isMobile ? `S. ${reference.page_number}` : `Referenz: Seite ${reference.page_number}`}
                size="small"
                color="primary"
                onClick={() => goToPage(reference.page_number!)}
                sx={{ cursor: "pointer" }}
              />
            </Box>
          )}
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        ref={containerRef}
        sx={isMobile ? { px: 0, py: 0, overflow: "auto", WebkitOverflowScrolling: "touch", "&.MuiDialogContent-root": { padding: 0 } } : undefined}
      >
        {loading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
            flexDirection="column"
            gap={2}
          >
            <Typography color="error">{error}</Typography>
            <Button variant="outlined" size="small" onClick={onClose}>
              Schließen
            </Button>
          </Box>
        )}

        {pdfUrl && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: isMobile ? 0.5 : 2,
              "& .react-pdf__Document": {
                display: "flex",
                flexDirection: "column",
                alignItems: isMobile ? "stretch" : "center",
                gap: isMobile ? 0.5 : 2,
                width: "100%",
              },
              "& .react-pdf__Page": {
                boxShadow: isMobile ? "none" : "0 2px 8px rgba(0,0,0,0.15)",
                backgroundColor: "white",
                maxWidth: "100%",
              },
            }}
          >
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={
                <Box
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  minHeight="400px"
                >
                  <CircularProgress />
                </Box>
              }
            >
              {pageNumbers.map((pageNum) => {
                const isReferencePage = pageNum === reference?.page_number;
                const isHighlightedPage = actualHighlightPages.has(pageNum);
                // Show border on highlighted pages, fallback to reference page if no highlights found yet
                const showBorder = actualHighlightPages.size > 0 ? isHighlightedPage : isReferencePage;

                return (
                  <Box
                    key={`page_${pageNum}`}
                    ref={(el: HTMLDivElement | null) => {
                      if (el) {
                        pageRefs.current.set(pageNum, el);
                      } else {
                        pageRefs.current.delete(pageNum);
                      }
                    }}
                    sx={{
                      border: showBorder
                        ? isMobile ? "none" : "3px solid #1976d2"
                        : isMobile ? "none" : "1px solid #e0e0e0",
                      borderRadius: isMobile ? 0 : 1,
                      overflow: "hidden",
                      position: "relative",
                      maxWidth: "100%",
                      width: isMobile ? "100%" : undefined,
                      "&::after": showBorder && isMobile ? {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        border: "3px solid #1976d2",
                        pointerEvents: "none",
                        zIndex: 20,
                      } : undefined,
                    }}
                  >
                    {/* Blue border is sufficient as reference indicator */}
                    <Page
                      pageNumber={pageNum}
                      scale={isMobile ? undefined : scale}
                      width={isMobile ? getPageWidth() : undefined}
                      renderTextLayer={true}
                      renderAnnotationLayer={!isMobile}
                      onRenderSuccess={() => handlePageRenderSuccess(pageNum)}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        textAlign: "center",
                        py: isMobile ? 0.25 : 1,
                        backgroundColor: "#f5f5f5",
                        fontSize: isMobile ? "0.65rem" : undefined,
                      }}
                    >
                      Seite {pageNum} von {numPages}
                    </Typography>
                  </Box>
                );
              })}
            </Document>
          </Box>
        )}
      </DialogContent>

      {!isMobile && (
        <DialogActions>
          <Button onClick={onClose}>Schließen</Button>
        </DialogActions>
      )}

      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={handleCloseFeedback}
        documentName={reference?.document_name || ""}
        documentTitle={documentTitle}
      />
    </Dialog>
  );
};

export default PdfViewer;
